import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, matchPath } from 'react-router-dom'
//contexts
import { useApplicationContext } from '../../../../context/ApplicationContext'
//components
import { Message } from '@paypalcorp/worldready-react'
import TopNavToggleMessage, {
  DEFAULT_DIALOG,
  WEBLLS_REMEMBERED_USER_DIALOG,
  UNAUTHORIZED_LIVE_ENV_ROUTE_APPS_DIALOG,
  UNAUTHORIZED_LIVE_ENV_ROUTE_SANDBOX_DIALOG,
  UNAUTHORIZED_LIVE_ENV_ROUTE_HOME_DIALOG,
  UNCONFIRMED_EMAIL_DIALOG,
  UNCONFIRMED_EMAIL_SECONDARY_USER_DIALOG,
  MINIMAL_ACCOUNT_DIALOG,
  MISSING_SB_ACCOUNT_DETAILS_DIALOG,
  LIVE_NOT_ALLOWED,
  STEPUP_NEEDED,
} from '../top-nav-toggle-message'
//utils
import _filter from 'lodash.filter'
import { getUserType } from '../../../../utils/user-utils'
import { clearLocalStorage } from '../../../../utils/session-utils'
//constants
import {
  topNavToggleItems,
  unauthorizedLiveEnvToggleUrls,
} from './top-nav-toggle-scheme'
import { recordDynamicEvent } from '../../analytics/utils/record-dynamic-event'
import { useComponentVisible } from '../../../../hooks'
import { SANDBOX_LIVE_TOGGLE_EXCLUDED_ROUTES } from '../../../../config/excludedRoutes'

function getSandboxBusinessAlert(enviroment, accountType) {
  return enviroment === 'sandbox' && accountType !== 'BUSINESS'
    ? { show: true, type: DEFAULT_DIALOG }
    : null
}

function getSandboxLiveApiAlert(enviroment, liveApiAllowed, stepUpNeeded) {
  if (enviroment === 'sandbox' && liveApiAllowed === false) {
    return {
      show: true,
      type: stepUpNeeded ? STEPUP_NEEDED : LIVE_NOT_ALLOWED,
    }
  }
  return null
}

function getUnconfirmedEmailAlert(isConfirmedEmail, secondaryUser) {
  if (!isConfirmedEmail) {
    return {
      show: true,
      type: secondaryUser
        ? UNCONFIRMED_EMAIL_SECONDARY_USER_DIALOG
        : UNCONFIRMED_EMAIL_DIALOG,
    }
  }
  return null
}

function getWebLLSUserAlert(enviroment, isWebLLSUser) {
  return enviroment === 'sandbox' && isWebLLSUser
    ? { show: true, type: WEBLLS_REMEMBERED_USER_DIALOG }
    : null
}

function getMinimalAccountAlert(processedMinAcct) {
  return processedMinAcct?.isMinAccount
    ? { show: true, type: MINIMAL_ACCOUNT_DIALOG }
    : null
}

function getRestrictedPathAlertType(
  enviroment,
  restrictedPathType,
  nvpSoapAppData,
) {
  const APP_CREDENTIALS = 'apps&credentials'
  const SANDBOX_ACCOUNT = 'sandboxAccounts'

  const isMissingSbAccountDetails =
    enviroment === 'live' &&
    restrictedPathType === APP_CREDENTIALS &&
    nvpSoapAppData?.openIdConnect === 'Y' &&
    !nvpSoapAppData?.sbCredentials?.client_id

  if (isMissingSbAccountDetails) {
    return MISSING_SB_ACCOUNT_DETAILS_DIALOG
  }
  if (restrictedPathType === APP_CREDENTIALS) {
    return UNAUTHORIZED_LIVE_ENV_ROUTE_APPS_DIALOG
  }
  if (restrictedPathType === SANDBOX_ACCOUNT) {
    return UNAUTHORIZED_LIVE_ENV_ROUTE_SANDBOX_DIALOG
  }
  return UNAUTHORIZED_LIVE_ENV_ROUTE_HOME_DIALOG
}

function getRestrictedPathAlert(
  enviroment,
  restrictedPathType,
  nvpSoapAppData,
) {
  if (!restrictedPathType) {
    return null
  }
  const type = getRestrictedPathAlertType(
    enviroment,
    restrictedPathType,
    nvpSoapAppData,
  )
  return { show: true, type }
}

function determineRestrictedPathType(
  pathname,
  search,
  nvpSoapAppData,
  unauthorizedUrls,
) {
  let restrictedPathType = null

  for (const restrictedURL of unauthorizedUrls) {
    const isPathMatch = matchPath(restrictedURL.url, pathname)
    if (!isPathMatch) {
      continue
    }

    restrictedPathType = restrictedURL.urlType

    const hasException =
      restrictedURL.exceptionUrlContainsParameters &&
      search.includes(restrictedURL.exceptionUrlContainsParameters) &&
      nvpSoapAppData?.sbCredentials?.client_id

    if (hasException) {
      restrictedPathType = null
    }
    break
  }

  return restrictedPathType
}

function shouldHandleLivePageForWebLLSUser(
  isLive,
  isWebLLSUser,
  liveApiAllowed,
) {
  return isLive && (isWebLLSUser || liveApiAllowed === false)
}

export const TopNavToggle = React.memo(() => {
  const {
    enviroment,
    setCurrentEnvironment,
    setReloadPage,
    currentBannerStep,
    user,
    isWebLLSUser,
    isConfirmedEmail,
    processedMinAcct,
    secondaryUser,
    liveApiAllowed,
    stepUpNeeded,
    nvpSoapAppData,
    setCurrentAppValue,
  } = useApplicationContext()
  const navigate = useNavigate()
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(true)

  const { pathname, search } = useLocation()
  const [onClickSwitcher, setOnClickSwitcher] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [dialog, setDialog] = useState(false)
  const accountType = getUserType(user)
  const isLive = enviroment === 'live'
  const isToggleHidden = SANDBOX_LIVE_TOGGLE_EXCLUDED_ROUTES.includes(pathname)
  const restrictedPathType = determineRestrictedPathType(
    pathname,
    search,
    nvpSoapAppData,
    unauthorizedLiveEnvToggleUrls,
  )

  const needAlert = () =>
    getSandboxBusinessAlert(enviroment, accountType) ||
    getSandboxLiveApiAlert(enviroment, liveApiAllowed, stepUpNeeded) ||
    getUnconfirmedEmailAlert(isConfirmedEmail, secondaryUser) ||
    getWebLLSUserAlert(enviroment, isWebLLSUser) ||
    getMinimalAccountAlert(processedMinAcct) ||
    getRestrictedPathAlert(enviroment, restrictedPathType, nvpSoapAppData) || {
      show: false,
      type: undefined,
    }

  const shouldReloadPage = () => {
    const closeOnboardingBanner =
      currentBannerStep?.closeOnboardingBanner || false
    const isOnboardingPage =
      currentBannerStep?.bannerPage === 2 || currentBannerStep?.bannerPage === 4
    return closeOnboardingBanner || !isOnboardingPage
  }

  const handleAlertScenario = (alert) => {
    recordDynamicEvent(
      `Environment-toggle,click,switch disabled - alert: ${alert.type}`,
    )
    setOnClickSwitcher(false)
    setOpenDialog(true)
    setIsComponentVisible(true)
    setDialog(alert.type)
  }

  useEffect(() => {
    if (!isComponentVisible) {
      setOpenDialog(false)
    }
  }, [isComponentVisible])

  const handleNavigation = (targetEnvironment, filteredResult) => {
    if (filteredResult.length === 0) {
      return
    }

    const targetUrl = filteredResult[0][targetEnvironment]
    // Remove the /dashboard from url to pass to navigate
    const urlParts = targetUrl.split('/')
    const navigateTo = '/' + urlParts.slice(2).join('/')
    const isSandboxTransaction = navigateTo.includes('transactions/sandbox')
    const destination = isSandboxTransaction ? '/' : navigateTo

    navigate(destination)
  }

  const handleEnvironmentSwitch = () => {
    const targetEnvironment = enviroment === 'sandbox' ? 'live' : 'sandbox'
    recordDynamicEvent(
      `Environment-toggle,click,environment toggle switch to ${targetEnvironment}`,
    )
    const filteredResult = _filter(topNavToggleItems, [
      enviroment,
      location.pathname,
    ])
    setCurrentEnvironment(targetEnvironment)
    setCurrentAppValue(undefined)

    if (shouldReloadPage()) {
      setReloadPage('yes')
    }

    handleNavigation(targetEnvironment, filteredResult)
    setOnClickSwitcher(true)
  }

  const onClickHandler = async () => {
    clearLocalStorage('appSelection')
    const alert = needAlert()
    const shouldShowAlert = alert.show === true

    if (shouldShowAlert) {
      handleAlertScenario(alert)
      return
    }

    handleEnvironmentSwitch()
  }

  const onCloseHandler = () => {
    setOpenDialog(false)
  }

  const handleLivePageForWebLLSUser = () => {
    setCurrentEnvironment('sandbox')
    setReloadPage('yes')
  }

  const renderDialog = () => {
    if (!openDialog) {
      return null
    }

    return (
      <TopNavToggleMessage
        dialogType={dialog}
        accountType={accountType}
        onCloseHandler={onCloseHandler}
      />
    )
  }

  const renderLiveToggle = () => (
    <div className="dev_dash__top-nav-switcher-block" id="toggle_sandbox_live">
      <div className="dev_dash__top-nav-switcher-label-gray-black">
        <Message id="top-nav.sandbox.label" />
      </div>
      <div
        className="dev_dash__top-nav-switcher-container"
        onClick={onClickHandler}
        data-cy="env-toggle-switch-sandbox"
      >
        <div className="dev_dash__top-nav-switcher-live-background">
          <div className="dev_dash__top-nav-switcher-live-circle" />
        </div>
      </div>
      <div className="dev_dash__top-nav-switcher-label-black-gray">
        <Message id="top-nav.live.label" />
      </div>
      {renderDialog()}
    </div>
  )

  const renderSandboxToggle = () => (
    <div className="dev_dash__top-nav-switcher-block" id="toggle_sandbox_live">
      <div className="dev_dash__top-nav-switcher-label-black-gray">
        <Message id="top-nav.sandbox.label" />
      </div>
      <div
        className="dev_dash__top-nav-switcher-container"
        onClick={onClickHandler}
        data-cy="env-toggle-switch-sandbox"
      >
        <div className="dev_dash__top-nav-switcher-sandbox-background">
          <div className="dev_dash__top-nav-switcher-sandbox-circle" />
        </div>
      </div>
      <div className="dev_dash__top-nav-switcher-label-gray-black">
        <Message id="top-nav.live.label" />
      </div>
      <div ref={ref}>{renderDialog()}</div>
    </div>
  )

  const animationClasses = {
    sandboxLabel: isLive
      ? 'dev_dash__top-nav-switcher-label-black-gray-animation'
      : 'dev_dash__top-nav-switcher-label-gray-black-animation',
    liveLabel: isLive
      ? 'dev_dash__top-nav-switcher-label-gray-black-animation'
      : 'dev_dash__top-nav-switcher-label-black-gray-animation',
    background: isLive
      ? 'dev_dash__top-nav-switcher-sandbox-live-background-animation'
      : 'dev_dash__top-nav-switcher-live-sandbox-background-animation',
    circle: isLive
      ? 'dev_dash__top-nav-switcher-sandbox-live-circle-animation'
      : 'dev_dash__top-nav-switcher-sandbox-circle-animation',
  }

  const renderAnimatedToggle = () => (
    <div className="dev_dash__top-nav-switcher-block" id="toggle_sandbox_live">
      <div className={animationClasses.sandboxLabel}>
        <Message id="top-nav.sandbox.label" />
      </div>
      <div
        className="dev_dash__top-nav-switcher-container"
        onClick={onClickHandler}
        data-cy="env-toggle-switch-live"
      >
        <div className={animationClasses.background}>
          <div className={animationClasses.circle} />
        </div>
      </div>
      <div className={animationClasses.liveLabel}>
        <Message id="top-nav.live.label" />
      </div>
    </div>
  )

  const renderToggleContent = () => {
    const shouldHandleLivePage = shouldHandleLivePageForWebLLSUser(
      isLive,
      isWebLLSUser,
      liveApiAllowed,
    )

    if (shouldHandleLivePage) {
      handleLivePageForWebLLSUser()
    }

    if (onClickSwitcher) {
      return renderAnimatedToggle()
    }

    return isLive ? renderLiveToggle() : renderSandboxToggle()
  }

  if (isToggleHidden) {
    return null
  }

  return (
    <div className="dev_dash__top-nav-switcher-flex">
      {renderToggleContent()}
    </div>
  )
})

export default TopNavToggle
